import { createFormsApiClient } from "@repo/api-forms";
import { useGetFormConfigBySlug, getGetFormConfigBySlugQueryKey } from "@repo/api-forms/src/generated/hooks";
import { TypeformStyle } from "@repo/forms-ui";
import { useParams } from "@tanstack/react-router";
import React from "react";
import { WEB_API_BASE } from "../config";
import {
	formConfigToCollectionSchema,
	submissionToCollectionRecord,
} from "../lib/mapping/collections";
import { orgSlugParamsSchema } from "../lib/route-schemas";
import { uploadPresigned } from "../lib/uploads/presigned";
import { validateFile } from "../lib/uploads/validation";
import { createUploadFileId } from "../lib/uploads/fileId";
import { buildDefaultHeaders } from "../lib/apiClient";
import type { FormConfig, FormField } from "../types/form";

// Instantiate lazily to allow tests to mock createFormsApiClient before usage
function useFormsApi() {
	const defaultHeaders = React.useMemo(() => buildDefaultHeaders(), []);
  return React.useMemo(
    () =>
      createFormsApiClient({
        baseUrl: `${WEB_API_BASE}/api`,
        defaultHeaders,
      }),
    [defaultHeaders],
  );
}

export default function FormRoute() {
	const { org, slug } = useParams({ from: "/forms/$org/$slug" });
  const formsApi = useFormsApi();

	const parsedParams = React.useMemo(
		() => orgSlugParamsSchema.safeParse({ org, slug }),
		[org, slug],
	);
	const invalid = !parsedParams.success;

	const {
		data: config,
		isLoading,
		error,
  } = useGetFormConfigBySlug(
    org,
    slug,
    {
      axios: { baseURL: `${WEB_API_BASE}` },
      query: { queryKey: getGetFormConfigBySlugQueryKey(org, slug), enabled: parsedParams.success },
    },
  );

	// Ensure config is properly typed
	const typedConfig = config as FormConfig | undefined;

	const handleFileUpload = async (
		fieldId: string,
		file: File,
		onProgress?: (pct: number) => void,
	) => {
		if (!typedConfig) {
			return { success: false, error: "Form configuration not loaded" };
		}

		// Find the field configuration
		const field = findFieldById(typedConfig, fieldId);
		if (!field) {
			return { success: false, error: `Field '${fieldId}' not found in form` };
		}

		// Validate the file
		const validation = validateFile(file, field);
		if (!validation.success) {
			return { success: false, error: validation.error ?? "Invalid file" };
		}

		try {
			// Get presigned URL from backend
			const presignResponse = await formsApi.presignUpload(org, slug, {
				fieldId,
				fileName: file.name,
				contentType: file.type,
				size: file.size,
			});
			const statusOk =
				presignResponse.status >= 200 && presignResponse.status < 300;

			if (!statusOk) {
				return {
					success: false,
					error: "Failed to get upload URL from server",
				};
			}

			// Upload file to S3
			if (!presignResponse.data) {
				return {
					success: false,
					error: "Invalid presigned URL response from server",
				};
			}

			const presumedFileId =
				presignResponse.data.fields?.key ??
				presignResponse.data.headers?.["x-amz-meta-file-id"] ??
				undefined;
			const uploadResult = await uploadPresigned(
				presignResponse.data,
				file,
				onProgress,
			);

			if (uploadResult.success) {
				const resolvedFileId = createUploadFileId(
					fieldId,
					presumedFileId ?? uploadResult.fileId,
				);
				return {
					success: true,
					fileId: resolvedFileId,
					...(uploadResult.url ? { url: uploadResult.url } : {}),
				};
			}
			return {
				success: false,
				error: uploadResult.error || "File upload failed",
			};
		} catch (error) {
			console.error("File upload error:", error);

			// Handle different types of errors
			if (error instanceof Error) {
				if (
					error.message.includes("NetworkError") ||
					error.message.includes("fetch")
				) {
					return {
						success: false,
						error: "Network error. Please check your connection and try again.",
					};
				}
				if (error.message.includes("timeout")) {
					return {
						success: false,
						error: "Upload timed out. Please try again.",
					};
				}
				return { success: false, error: `Upload failed: ${error.message}` };
			}

			return {
				success: false,
				error: "An unexpected error occurred during upload",
			};
		}
	};

	const handleSubmit = async (data: Record<string, unknown>) => {
		if (!typedConfig)
			return { success: false, error: "Form configuration not loaded" };

		// Map to collection schema & record for portability
		const schema = formConfigToCollectionSchema(typedConfig);
		const record = submissionToCollectionRecord(typedConfig, data);

		try {
			const formId = typedConfig.id || `${org}-${slug}`;
			const response = await formsApi.submitFormBySlug(org, slug, {
				formId,
				data: record as Record<string, unknown>,
			});

			if (response.status === 200) {
				const submissionId = (response.data as { submission_id?: string })
					?.submission_id;
				return submissionId
					? { success: true, submissionId }
					: { success: true };
			}

			// Handle non-200 responses
			let errorMessage = "Submission failed";
			let validationErrors: Array<{ field: string; message: string }> = [];

			if (response.data && typeof response.data === "object") {
				const errorData = response.data as {
					error?: string;
					validation_errors?: Array<{ field: string; message: string }>;
				};
				if (
					errorData.error === "Validation failed" &&
					errorData.validation_errors
				) {
					validationErrors = errorData.validation_errors;
					errorMessage = "Please correct the following errors:";
				} else if (errorData.error) {
					errorMessage = errorData.error;
				} else {
					errorMessage = `Server error: ${response.status}`;
				}
			} else {
				errorMessage = `HTTP ${response.status}: Submission failed`;
			}

			console.error("Submit failed", response.status, errorMessage);
			const result: {
				success: false;
				error: string;
				validationErrors?: Array<{ field: string; message: string }>;
			} = {
				success: false,
				error: errorMessage,
			};
			if (validationErrors.length > 0)
				result.validationErrors = validationErrors;
			return result;
		} catch (error) {
			console.error("Submit error", error);

			// Fallback: attempt direct fetch submission if the typed client failed
			let lastError: unknown = error;
            try {
                const headers = new Headers(formsApi.buildAuthHeaders("POST"));
                headers.set("Content-Type", "application/json");
                headers.set("Accept", "application/json");
                const res = await fetch(
					`${WEB_API_BASE}/api/forms/${org}/${slug}/submit`,
					{
						method: "POST",
						headers,
						credentials: "include",
						body: JSON.stringify({
							formId: typedConfig.id || `${org}-${slug}`,
							data: record,
						}),
					},
				);
				if (res.ok) {
					let body: { submission_id?: string } | null = null;
					try {
						body = (await res.json()) as { submission_id?: string };
					} catch {
						body = null;
					}
					return body?.submission_id
						? { success: true, submissionId: body.submission_id }
						: { success: true };
				}
				// Attempt to extract validation errors from fallback response
				try {
					const errBody = (await res.json()) as {
						error?: string;
						validation_errors?: Array<{ field: string; message: string }>;
					};
					if (
						errBody?.error === "Validation failed" &&
						Array.isArray(errBody.validation_errors)
					) {
						return {
							success: false,
							error: "Please correct the following errors:",
							validationErrors: errBody.validation_errors,
						};
					}
				} catch {}
			} catch (e) {
				// Capture fallback error for messaging
				lastError = e;
			}

			// Determine error type for better user messaging
			let errorMessage = "An unexpected error occurred";
			const err = (lastError instanceof Error ? lastError : error) as Error | unknown;
			if (err instanceof Error) {
				const msg = err.message || "";
				if (
					msg.includes("NetworkError") ||
					msg.toLowerCase().includes("network error") ||
					msg.includes("fetch")
				) {
					errorMessage =
						"Network error. Please check your connection and try again.";
				} else if (err.message.includes("timeout")) {
					errorMessage = "Request timed out. Please try again.";
				} else if (err.message.includes("404")) {
					errorMessage = "Form not found. Please check the URL.";
				} else if (err.message.includes("400")) {
					errorMessage = "Invalid submission data. Please check your inputs.";
				} else {
					errorMessage = err.message;
				}
			}

			return { success: false, error: errorMessage };
		}
	};

	if (invalid) {
		return (
			<div style={{ color: "#b91c1c", padding: 16 }}>
				Invalid form URL. Please check the organization and form slug.
			</div>
		);
	}
	if (error)
		return (
			<div style={{ color: "#b91c1c", padding: 16 }}>
				Failed to load form: {String(error)}
			</div>
		);
	if (isLoading || !typedConfig)
		return <div style={{ padding: 16 }}>Loading…</div>;

	// Render Typeform-like UI by default for a slick single-question flow
	return (
		<TypeformStyle
			config={typedConfig}
			onSubmit={handleSubmit}
			onFileUpload={({ fieldId, file }) => handleFileUpload(fieldId, file)}
		/>
	);
}

// Helper function to find a field by ID in the form configuration
function findFieldById(
	config: FormConfig,
	fieldId: string,
): FormField | undefined {
	for (const section of config.sections) {
		for (const field of section.fields) {
			if (field.id === fieldId) {
				return field;
			}
		}
	}
	return undefined;
}
